/*
 * Social Login Buttons Component
 * 
 * Provides Google and Facebook login buttons that are conditionally shown
 * based on admin configuration. Handles the complete OAuth flow.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/config';

const SocialLoginButtons = ({ onLoginSuccess, onLoginError }) => {
  const [socialConfig, setSocialConfig] = useState({
    google_enabled: false,
    facebook_enabled: false,
    google_client_id: null,
    facebook_app_id: null
  });
  const [loading, setLoading] = useState(false);

  // Load social login configuration
  useEffect(() => {
    loadSocialConfig();
  }, []);

  const loadSocialConfig = async () => {
    try {
      const response = await axios.get(`${API}/auth/social/config`);
      setSocialConfig(response.data);
    } catch (error) {
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      // Get Google auth URL
      const response = await axios.get(`${API}/auth/social/google/login`);
      const authUrl = response.data.auth_url;

      // Open popup window for OAuth
      const popup = window.open(
        authUrl,
        'google-login',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );

      // Listen for OAuth callback
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          setLoading(false);
        }
      }, 1000);

      // Listen for postMessage from popup
      window.addEventListener('message', handleOAuthCallback);

    } catch (error) {
      onLoginError('Google login failed');
      setLoading(false);
    }
  };

  const handleFacebookLogin = async () => {
    setLoading(true);
    try {
      // Get Facebook auth URL
      const response = await axios.get(`${API}/auth/social/facebook/login`);
      const authUrl = response.data.auth_url;

      // Open popup window for OAuth
      const popup = window.open(
        authUrl,
        'facebook-login',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );

      // Listen for OAuth callback
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          setLoading(false);
        }
      }, 1000);

      // Listen for postMessage from popup
      window.addEventListener('message', handleOAuthCallback);

    } catch (error) {
      onLoginError('Facebook login failed');
      setLoading(false);
    }
  };

  const handleOAuthCallback = async (event) => {
    // Verify origin for security
    const allowedOrigins = [
      window.location.origin,
      'https://rideya.bz'
    ];

    if (!allowedOrigins.includes(event.origin)) {
      return;
    }

    const { provider, code, error } = event.data;

    if (error) {
      onLoginError(`${provider} login failed: ${error}`);
      setLoading(false);
      return;
    }

    if (code && provider) {
      try {
        const redirectUri = `${window.location.origin}/auth/${provider}/callback`;

        // Exchange code for access token
        const response = await axios.post(`${API}/auth/social/${provider}/callback`, {
          code,
          redirect_uri: redirectUri
        });

        // Set auth token and user data
        const { access_token, user, is_new_user } = response.data;

        // Store token in localStorage
        localStorage.setItem('token', access_token);

        // Set default authorization header
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

        // Call success callback
        onLoginSuccess(user, is_new_user);

      } catch (error) {
        onLoginError(`${provider} authentication failed`);
      }
    }

    setLoading(false);
    window.removeEventListener('message', handleOAuthCallback);
  };

  // Don't render anything if no providers are enabled
  if (!socialConfig.google_enabled && !socialConfig.facebook_enabled) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">Or continue with</span>
        </div>
      </div>

      <div className="space-y-2">
        {socialConfig.google_enabled && (
          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {loading ? 'Signing in...' : 'Continue with Google'}
          </button>
        )}

        {socialConfig.facebook_enabled && (
          <button
            onClick={handleFacebookLogin}
            disabled={loading}
            className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
            {loading ? 'Signing in...' : 'Continue with Facebook'}
          </button>
        )}
      </div>
    </div>
  );
};

export default SocialLoginButtons;